import React, { useCallback } from 'react';
import {
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import { Header } from '../components/GiftWrapModal/Header';
import { Footer } from '../components/GiftWrapModal/Footer';
import { UvInsertsNavContextProvider } from './context/UvInsertsNavContext';
import { useFlowBridge } from './context/FlowBridgeContext';
import { useUvInsertsContext } from './context/UvInsertsContext';
import { UvInsertChooser } from '@uv-inserts/components/UvInsertChooser';
import { getSuggestionTargetTextPropertyKeyForRegionTab } from '@shared/view/uv-inserts/visualiser-tabs';
import { OptionSelectionRoute } from './pages/OptionSelectionRoute';
import { UvInsertVisualiserPage } from './pages/UvInsertVisualiserPage';
import styles from '../components/GiftWrapModal/modal.module.css';

/**
 * Renders the UvInsertChooser (theme grid + suggestion groups) as a direct flex
 * child of the modal — outside the scrollable content area. This means when the
 * chooser opens it expands upward within the fixed modal height, pushing the
 * scrollable visualiser area up rather than requiring the user to scroll down.
 * Renders nothing when no visualiser config is active (option-selection pages).
 */
function UvInsertChooserSlot() {
  const {
    resolved,
    activeConfig,
    activeTabIndex,
    themeIndex,
    setThemeIndex,
    textByKey,
    setText,
    themeChooserOpen,
    setThemeChooserOpen,
    suggestionChooserOpen,
    setSuggestionChooserOpen,
    selectedSuggestionGroupIndex,
    setSelectedSuggestionGroupIndex,
  } = useUvInsertsContext();

  const onSuggestionPick = useCallback((text: string) => {
    if (!activeConfig) return;
    const key = getSuggestionTargetTextPropertyKeyForRegionTab({
      faceElements: activeConfig.faceElements,
      tabIndex: activeTabIndex,
    });
    if (key) setText(key, text);
  }, [activeConfig, activeTabIndex, setText]);

  if (!activeConfig || !resolved) return null;

  return (
    <div className={styles['giftWrap__chooser-slot']}>
      <UvInsertChooser
        config={activeConfig}
        tabIndex={activeTabIndex}
        suggestedMessaging={resolved.suggestedMessaging}
        selectedThemeIndex={themeIndex}
        onThemeIndexChange={setThemeIndex}
        themeChooserOpen={themeChooserOpen}
        onThemeChooserOpenChange={setThemeChooserOpen}
        selectedSuggestionGroupIndex={selectedSuggestionGroupIndex}
        onSelectedSuggestionGroupIndexChange={setSelectedSuggestionGroupIndex}
        suggestionChooserOpen={suggestionChooserOpen}
        onSuggestionChooserOpenChange={setSuggestionChooserOpen}
        onSuggestionPick={onSuggestionPick}
      />
    </div>
  );
}

/**
 * Root layout — rendered inside RouterProvider, so TanStack router hooks work.
 * Wraps Header, Outlet (route content), and Footer with UvInsertsNavContextProvider,
 * which overrides PageContext so they navigate via the memory router.
 *
 * UvInsertChooserSlot sits between the scrollable content and the footer so the
 * chooser panel never lives inside the scroll container — opening it never requires
 * the user to scroll down to see it.
 */
function RootLayout() {
  const { onClose } = useFlowBridge();
  return (
    <UvInsertsNavContextProvider>
      <Header onClose={onClose} />
      <div className={styles['giftWrap__modal-content']}>
        <Outlet />
      </div>
      <UvInsertChooserSlot />
      <Footer />
    </UvInsertsNavContextProvider>
  );
}

// Root route renders the modal shell.
const rootRoute = createRootRoute({ component: RootLayout });

// ── Option selection pages ──────────────────────────────────────────────────
const addPersonalTouchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/addPersonalTouch',
  component: () => <OptionSelectionRoute pageName="addPersonalTouch" />,
});

const boxInsertUploadOrTextRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertUploadOrText',
  component: () => <OptionSelectionRoute pageName="boxInsertUploadOrText" />,
});

const boxInsertPhotoBlankOrThemedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertPhotoBlankOrThemed',
  component: () => <OptionSelectionRoute pageName="boxInsertPhotoBlankOrThemed" />,
});

const boxInsertTextBlankOrThemedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertTextBlankOrThemed',
  component: () => <OptionSelectionRoute pageName="boxInsertTextBlankOrThemed" />,
});

const cardChoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardChoice',
  component: () => <OptionSelectionRoute pageName="cardChoice" />,
});

const cardPhotoUploadThemedOrBlankRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardPhotoUploadThemedOrBlank',
  component: () => <OptionSelectionRoute pageName="cardPhotoUploadThemedOrBlank" />,
});

const cardTextOnlyThemedOrBlankRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardTextOnlyThemedOrBlank',
  component: () => <OptionSelectionRoute pageName="cardTextOnlyThemedOrBlank" />,
});

const cardBlankMessageChoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardBlankMessageChoice',
  component: () => <OptionSelectionRoute pageName="cardBlankMessageChoice" />,
});

const boxInsertThemedPhotoSeasonalOrBridalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertThemedPhotoSeasonalOrBridal',
  component: () => <OptionSelectionRoute pageName="boxInsertThemedPhotoSeasonalOrBridal" />,
});

const boxInsertThemedTextSeasonalOrBridalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertThemedTextSeasonalOrBridal',
  component: () => <OptionSelectionRoute pageName="boxInsertThemedTextSeasonalOrBridal" />,
});

const cardThemedPhotoSeasonalOrBridalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardThemedPhotoSeasonalOrBridal',
  component: () => <OptionSelectionRoute pageName="cardThemedPhotoSeasonalOrBridal" />,
});

const cardThemedTextSeasonalOrBridalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardThemedTextSeasonalOrBridal',
  component: () => <OptionSelectionRoute pageName="cardThemedTextSeasonalOrBridal" />,
});

// ── Visualiser pages ────────────────────────────────────────────────────────
const boxInsertBlankMessageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertBlankMessage',
  component: () => <UvInsertVisualiserPage routeName="boxInsertBlankMessage" />,
});

const boxInsertBlankPhotoUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertBlankPhotoUpload',
  component: () => <UvInsertVisualiserPage routeName="boxInsertBlankPhotoUpload" />,
});

const boxInsertThemedMessageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertThemedMessage',
  component: () => <UvInsertVisualiserPage routeName="boxInsertThemedMessage" />,
});

const boxInsertThemedPhotoUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertThemedPhotoUpload',
  component: () => <UvInsertVisualiserPage routeName="boxInsertThemedPhotoUpload" />,
});

const cardBlankCustomMessageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardBlankCustomMessage',
  component: () => <UvInsertVisualiserPage routeName="cardBlankCustomMessage" />,
});

const cardBlankPhotoUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardBlankPhotoUpload',
  component: () => <UvInsertVisualiserPage routeName="cardBlankPhotoUpload" />,
});

const cardBlankPredesignedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardBlankPredesigned',
  component: () => <UvInsertVisualiserPage routeName="cardBlankPredesigned" />,
});

const cardThemedCustomMessageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardThemedCustomMessage',
  component: () => <UvInsertVisualiserPage routeName="cardThemedCustomMessage" />,
});

const cardThemedPhotoUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardThemedPhotoUpload',
  component: () => <UvInsertVisualiserPage routeName="cardThemedPhotoUpload" />,
});

const boxInsertBridalTextImageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertBridalTextImage',
  component: () => <UvInsertVisualiserPage routeName="boxInsertBridalTextImage" />,
});

const boxInsertBridalTextOnlyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertBridalTextOnly',
  component: () => <UvInsertVisualiserPage routeName="boxInsertBridalTextOnly" />,
});

const cardBridalTextImageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardBridalTextImage',
  component: () => <UvInsertVisualiserPage routeName="cardBridalTextImage" />,
});

const cardBridalTextOnlyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardBridalTextOnly',
  component: () => <UvInsertVisualiserPage routeName="cardBridalTextOnly" />,
});

const boxInsertMensBlankMessageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertMensBlankMessage',
  component: () => <UvInsertVisualiserPage routeName="boxInsertMensBlankMessage" />,
});

const boxInsertMensBlankPhotoUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boxInsertMensBlankPhotoUpload',
  component: () => <UvInsertVisualiserPage routeName="boxInsertMensBlankPhotoUpload" />,
});

const cardMensBlankCustomMessageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardMensBlankCustomMessage',
  component: () => <UvInsertVisualiserPage routeName="cardMensBlankCustomMessage" />,
});

const cardMensBlankPhotoUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cardMensBlankPhotoUpload',
  component: () => <UvInsertVisualiserPage routeName="cardMensBlankPhotoUpload" />,
});

// ── Route tree ──────────────────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  addPersonalTouchRoute,
  boxInsertUploadOrTextRoute,
  boxInsertPhotoBlankOrThemedRoute,
  boxInsertTextBlankOrThemedRoute,
  cardChoiceRoute,
  cardPhotoUploadThemedOrBlankRoute,
  cardTextOnlyThemedOrBlankRoute,
  cardBlankMessageChoiceRoute,
  boxInsertThemedPhotoSeasonalOrBridalRoute,
  boxInsertThemedTextSeasonalOrBridalRoute,
  cardThemedPhotoSeasonalOrBridalRoute,
  cardThemedTextSeasonalOrBridalRoute,
  boxInsertBlankMessageRoute,
  boxInsertBlankPhotoUploadRoute,
  boxInsertMensBlankMessageRoute,
  boxInsertMensBlankPhotoUploadRoute,
  boxInsertThemedMessageRoute,
  boxInsertThemedPhotoUploadRoute,
  boxInsertBridalTextImageRoute,
  boxInsertBridalTextOnlyRoute,
  cardBlankCustomMessageRoute,
  cardBlankPhotoUploadRoute,
  cardMensBlankCustomMessageRoute,
  cardMensBlankPhotoUploadRoute,
  cardBlankPredesignedRoute,
  cardThemedCustomMessageRoute,
  cardThemedPhotoUploadRoute,
  cardBridalTextImageRoute,
  cardBridalTextOnlyRoute,
]);

/**
 * Creates a fresh TanStack memory router starting at /addPersonalTouch.
 * Called once per UvInsertsFlow mount so state resets each time the modal opens.
 * @returns {ReturnType<typeof createRouter>} A new configured memory router instance.
 */
export function createUvInsertsRouter() {
  return createRouter({
    routeTree,
    history: createMemoryHistory({ initialEntries: ['/addPersonalTouch'] }),
  });
}
